import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useCatalogueInteractions } from '../hooks/useCatalogueInteractions';
import { useCatalogueDataContext } from './CatalogueDataContext';
import { useAppDispatch } from '@store/store.tsx';

export interface CatalogueInteractionContextValue {
  loadMore: () => void;
  isLoading: boolean;
  hasMore: boolean;
}

interface CatalogueInteractionProviderProps {
  children: ReactNode;
  valueOverrides?: Partial<CatalogueInteractionContextValue>;
}

const CatalogueInteractionContext = createContext<CatalogueInteractionContextValue | undefined>(
  undefined,
);

export const CatalogueInteractionProvider = ({
  children,
  valueOverrides,
}: CatalogueInteractionProviderProps) => {
  const dispatch = useAppDispatch();
  const { page, categorySelectedId, isItemLoading, hasMore } = useCatalogueDataContext();

  const { handleLoadMore } = useCatalogueInteractions({
    dispatch,
    page,
    categorySelectedId,
  });

  const value = useMemo<CatalogueInteractionContextValue>(
    () => ({
      loadMore: handleLoadMore,
      isLoading: isItemLoading,
      hasMore,
    }),
    [handleLoadMore, hasMore, isItemLoading],
  );

  const mergedValue = useMemo<CatalogueInteractionContextValue>(
    () => ({
      ...value,
      ...(valueOverrides ?? {}),
    }),
    [value, valueOverrides],
  );

  return (
    <CatalogueInteractionContext.Provider value={mergedValue}>
      {children}
    </CatalogueInteractionContext.Provider>
  );
};

export const useCatalogueInteractionContext = (): CatalogueInteractionContextValue => {
  const context = useContext(CatalogueInteractionContext);

  if (!context) {
    throw new Error(
      'useCatalogueInteractionContext must be used within a CatalogueInteractionProvider',
    );
  }

  return context;
};
